import "./instrument";

import { createInertiaApp, type ResolvedComponent } from "@inertiajs/react";
import { initFromPageProps, ModalStackProvider } from "@inertiaui/modal-react";
import * as ActiveStorage from "@rails/activestorage";
import * as Sentry from "@sentry/react";
import { configureI18n, switchLocale } from "@travily/i18n";
import type { ReactNode } from "react";
import { StrictMode } from "react";
import { hydrateRoot } from "react-dom/client";
import { MapHost } from "@/layouts/map-host";
import PersistentLayout from "@/layouts/persistent-layout";
import { dockedChatHeaders } from "@/lib/message-dock-store";
import { registerNetworkErrorHandling } from "@/lib/network-error-handling";
import { initScrollRestore, scrollRestoreHeaders } from "@/lib/scroll-restore";
import { resolveViewTransition } from "@/lib/view-transition";
import enTranslations from "@/locales/en.json";

ActiveStorage.start();

// Configure i18n eagerly with English so t() works immediately.
// The locale is switched to the user's preference in setup() below.
configureI18n(enTranslations);

registerNetworkErrorHandling();

const localeModules = import.meta.glob<{ default: Record<string, unknown> }>("../locales/*.json", { eager: false });

async function loadAndSwitchLocale(locale: string): Promise<void> {
  if (locale === "en") return;

  const path = `../locales/${locale}.json`;
  const loader = localeModules[path];
  if (loader) {
    const localeModule = await loader();
    switchLocale(locale, localeModule.default);
  }
}

void createInertiaApp({
  title: (title) => (title ? title : "Travily"),

  resolve: (name) => {
    const pages = import.meta.glob<{ default: ResolvedComponent }>(
      ["../pages/**/*.tsx", "!../pages/**/*.view.tsx", "!../pages/**/*.stories.tsx"],
      { eager: true },
    );
    const page = pages[`../pages/${name}.tsx`];
    if (!page) {
      throw new Error(`Missing Inertia page component: '${name}.tsx'`);
    }

    page.default.layout ||= (page: ReactNode) => <PersistentLayout>{page}</PersistentLayout>;

    return page;
  },

  setup({ el, App, props }) {
    // Switch locale before first render if user has a non-English preference.
    const locale = (props.initialPage.props as Record<string, unknown>).locale as string | undefined;
    const renderApp = () => {
      const errorOptions = {
        onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
          console.warn("Uncaught error", error, errorInfo.componentStack);
        }),
        onCaughtError: Sentry.reactErrorHandler(),
        onRecoverableError: Sentry.reactErrorHandler(),
      };

      initFromPageProps(props as Parameters<typeof initFromPageProps>[0]);
      initScrollRestore(props.initialPage);

      hydrateRoot(
        el,
        <StrictMode>
          <ModalStackProvider>
            <MapHost>
              <App {...props} />
            </MapHost>
          </ModalStackProvider>
        </StrictMode>,
        errorOptions,
      );
    };

    if (locale && locale !== "en") {
      loadAndSwitchLocale(locale).then(renderApp, renderApp);
    } else {
      renderApp();
    }
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
    },
    // Evaluated per visit and merged over the caller's own options, so the docked chat's headers
    // are re-read outside React on every navigation — and the caller's headers have to be spread
    // back in by hand or this callback would drop them.
    visitOptions: (href, options) => ({
      viewTransition: resolveViewTransition(options),
      headers: {
        ...options.headers,
        ...dockedChatHeaders({ href, isPrefetch: options.prefetch === true }),
        // Mutations only (non-GET); note a later `only:` on a mutation visit
        // would make the redirect's re-render partial and defeat the restore.
        ...scrollRestoreHeaders({ method: options.method }),
      },
    }),
  },
}).catch((error) => {
  // Non-Inertia pages can load this bundle without a root element; only a missing #app on a
  // real Inertia page is a genuine boot failure.
  if (document.getElementById("app")) {
    throw error;
  } else {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    );
  }
});
